/* @jsxImportSource @emotion/react */

import React from 'react'
import { useIntl } from 'react-intl'
import { navigate } from 'vike/client/router'

import { Breadcrumb, BreadcrumbItem, CustomNoteIcon } from '@slite/components'
import { SearchIcon } from '@slite/icons'

import {
  helpArticlePathParams,
  splitHelpHighlight,
} from '../../data/helpCenterFormat'
import type { HelpSearchHit } from '../../data/sliteHelpCenter'
import { useRoutes } from '../../routes'

const SEARCH_DEBOUNCE_MS = 250
const MODAL_WIDTH = 640

function HelpHighlight({ highlight }: { highlight: string }) {
  return (
    <>
      {splitHelpHighlight(highlight).map((segment, index) =>
        segment.isMatch ? (
          <span
            key={`${index}-${segment.text}`}
            className="shade-10 font-weight-medium"
          >
            {segment.text}
          </span>
        ) : (
          <React.Fragment key={`${index}-${segment.text}`}>
            {segment.text}
          </React.Fragment>
        )
      )}
    </>
  )
}

export function HelpSearchModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const intl = useIntl()
  const routes = useRoutes()
  const [query, setQuery] = React.useState('')
  const [hits, setHits] = React.useState<HelpSearchHit[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const rowRefs = React.useRef<Array<HTMLAnchorElement | null>>([])
  const isKeyboardNav = React.useRef(false)
  const latestRequestRef = React.useRef(0)

  const articleHref = (hit: HelpSearchHit) =>
    routes.helpCenter.article(helpArticlePathParams(hit))

  React.useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
    } else {
      setQuery('')
      setHits([])
      setSelectedIndex(0)
    }
  }, [isOpen])

  // Keep the keyboard-selected result in view (but don't fight the mouse).
  React.useEffect(() => {
    if (isKeyboardNav.current) {
      rowRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  React.useEffect(() => {
    if (!isOpen) {
      return
    }
    const trimmed = query.trim()
    if (trimmed === '') {
      setHits([])
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    const requestId = ++latestRequestRef.current
    const timeout = setTimeout(async () => {
      try {
        const response = await fetch(
          `/help/api/search?query=${encodeURIComponent(trimmed)}`
        )
        if (!response.ok || requestId !== latestRequestRef.current) {
          return
        }
        const { hits: nextHits } = (await response.json()) as {
          hits: HelpSearchHit[]
        }
        if (requestId === latestRequestRef.current) {
          setHits(nextHits)
          setSelectedIndex(0)
        }
      } catch {
        // keep previous results on transient errors
      } finally {
        if (requestId === latestRequestRef.current) {
          setIsLoading(false)
        }
      }
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [query, isOpen])

  if (!isOpen) {
    return null
  }

  const moveSelection = (delta: number) => {
    isKeyboardNav.current = true
    setSelectedIndex((index) =>
      Math.max(0, Math.min(index + delta, hits.length - 1))
    )
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveSelection(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveSelection(-1)
    } else if (event.key === 'Enter') {
      const hit = hits[selectedIndex]
      if (hit) {
        navigate(articleHref(hit))
        onClose()
      }
    } else if (event.key === 'Escape') {
      onClose()
    }
  }

  const showEmptyState = !isLoading && query.trim() !== '' && hits.length === 0
  const cardStyle = {
    background: 'var(--shade-100)',
    borderRadius: 12,
    boxShadow: '0 16px 70px rgba(0, 0, 0, 0.2)',
  }

  return (
    <div
      css={{ position: 'fixed', inset: 0, zIndex: 1100 }}
      role="dialog"
      aria-modal="true"
      aria-label={intl.formatMessage({
        id: 'website.helpCenter.search.title',
        defaultMessage: 'Search the help center',
      })}
    >
      <div
        css={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(38, 42, 48, 0.4)',
        }}
        onClick={onClose}
      />
      <div
        className="flexbox flexbox-column flexbox-gap-8 margin-horizontal-16-small"
        css={{
          position: 'relative',
          maxWidth: MODAL_WIDTH,
          margin: '64px auto 0',
        }}
        onKeyDown={handleKeyDown}
      >
        <div
          className="flexbox flexbox-align-center flexbox-gap-12 padding-horizontal-20"
          css={{ ...cardStyle, height: 54 }}
        >
          <SearchIcon size={18} className="shade-30 flexbox-no-shrink" />
          <input
            data-test-id="helpSearchInput"
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={intl.formatMessage({
              id: 'website.helpCenter.search.placeholder',
              defaultMessage: 'Search articles…',
            })}
            className="hard-disable-outline border-none background-transparent width-full shade-05 font-size-6"
            css={{ '::placeholder': { color: 'var(--shade-30)' } }}
          />
        </div>

        {(hits.length > 0 || showEmptyState) && (
          <div
            css={{
              ...cardStyle,
              maxHeight: 440,
              overflowY: 'auto',
              padding: 8,
            }}
          >
            {hits.map((hit, index) => (
              <a
                key={hit.id}
                ref={(element) => {
                  rowRefs.current[index] = element
                }}
                data-test-id="helpSearchHit"
                href={articleHref(hit)}
                onClick={onClose}
                onMouseEnter={() => {
                  isKeyboardNav.current = false
                  setSelectedIndex(index)
                }}
                className="flexbox flexbox-gap-12 radius-large padding-horizontal-16 padding-vertical-12"
                css={{
                  textDecoration: 'none',
                  background:
                    index === selectedIndex
                      ? 'var(--moon-dust)'
                      : 'transparent',
                }}
              >
                <CustomNoteIcon
                  iconShape={hit.iconShape}
                  iconColor={hit.iconColor}
                  size={18}
                  className="shade-20 flexbox-no-shrink margin-top-2"
                />
                <span className="flexbox flexbox-column min-width-zero">
                  <span
                    className="shade-05 font-weight-semibold line-height-large ellipsis"
                    css={{ fontSize: 15 }}
                  >
                    {hit.title}
                  </span>
                  <span className="margin-top-2 font-size-7 line-height-large shade-20 ellipsis">
                    <Breadcrumb disablePopover className="font-weight-normal">
                      {[
                        intl.formatMessage({
                          id: 'website.helpCenter.article.breadcrumb.home',
                          defaultMessage: 'Help Center',
                        }),
                        ...hit.parentTitles,
                      ].map((item, itemIndex) => (
                        <BreadcrumbItem
                          key={`${itemIndex}-${item}`}
                          renderLabel={(props) => (
                            <span
                              {...props}
                              className="font-size-7 line-height-large shade-20 ellipsis"
                            >
                              {item}
                            </span>
                          )}
                        />
                      ))}
                    </Breadcrumb>
                  </span>
                  {hit.highlight !== '' && (
                    <span
                      className="margin-top-2 font-size-7 line-height-large shade-30"
                      css={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      <HelpHighlight highlight={hit.highlight} />
                    </span>
                  )}
                </span>
              </a>
            ))}
            {showEmptyState && (
              <div className="padding-16 text-align-center font-size-6 shade-30">
                {intl.formatMessage({
                  id: 'website.helpCenter.search.empty',
                  defaultMessage: 'No articles found',
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
